import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('data-sm-menu-fade', '');

    onDocumentLoaded(this.#preloadImages);
    this.addEventListener('pointerover', this.#mouseOver);
    this.addEventListener('pointerout', this.#mouseOut);
    this.addEventListener('focusout', this.#contactFocusOut);
    document.addEventListener('pointerdown', this.#outsidePointerDown, true);
    document.addEventListener('keydown', this.#escapeKey);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelMenuFade();
    this.removeEventListener('pointerover', this.#mouseOver);
    this.removeEventListener('pointerout', this.#mouseOut);
    this.removeEventListener('focusout', this.#contactFocusOut);
    document.removeEventListener('pointerdown', this.#outsidePointerDown, true);
    document.removeEventListener('keydown', this.#escapeKey);
    clearTimeout(this.#mouseCloseTimer);
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #state = {
    activeItem: null,
  };

  #pointerIdleTimer;
  #lastPointer = { x: 0, y: 0 };

  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  #startPointerTracking(item, previousItem) {
    // Custom panels already have delegated pointerover/out and a close buffer.
    // Avoid the second, legacy body-wide tracking/synthetic-entry system there.
    const customPanel = findSubmenu(item)?.querySelector('.sm-mega, .sm-contact-menu');
    if (customPanel) {
      document.body.removeEventListener('pointermove', this.#onPointerMove);
      if (previousItem) this.#stopPointerTracking(previousItem);
      this.#stopPointerTracking(item);
      return;
    }
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  #clickOpen = false;
  #contactEditing = false;
  #menuFade = null;
  #fadeClosing = null;

  #cancelMenuFade() {
    if (this.#menuFade) {
      this.#menuFade.onfinish = null;
      this.#menuFade.cancel();
      this.#menuFade = null;
    }
    this.#fadeClosing = null;
  }

  #mouseOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const row = event.target.closest('.menu-list__list-item');
    // Descendant pointerover events are not fresh entries into a menu row.
    // This also prevents a click-closed menu reopening as its label is crossed.
    if (row && event.relatedTarget instanceof Node && row.contains(event.relatedTarget)) return;
    const item = row ? findMenuItem(row) : null;
    if (!item || item.closest('header-menu') !== this) return;
    const submenu = findSubmenu(item);
    if (!submenu) return;
    clearTimeout(this.#mouseCloseTimer);
    this.#clickOpen = false;
    this.activate({ target: item.parentElement, menuItem: item, menuSubmenu: submenu });
  };

  #mouseOut = (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = this.#state.activeItem;
    if (!item) return;
    const submenu = findSubmenu(item);
    if (this.#contactEditing && submenu?.querySelector('.sm-contact-menu')) return;
    const next = event.relatedTarget;
    if (next instanceof Node && (item.contains(next) || submenu?.contains(next))) return;
    this.#clickOpen = false;
    clearTimeout(this.#mouseCloseTimer);
    this.#mouseCloseTimer = setTimeout(() => {
      if (this.#state.activeItem !== item) return;
      if (item.matches(':hover') || submenu?.matches(':hover')) return;
      // Keep the menu open while using a native dropdown,
      // including Garage options extending outside the panel.
      const focused = document.activeElement;

      if (
        submenu &&
        focused instanceof HTMLSelectElement &&
        submenu.contains(focused)
      ) return;

      if (
        submenu?.querySelector('.sm-contact-menu') &&
        submenu.contains(focused)
      ) return;

      this.#deactivate(item);
    }, 80);
  };

  #contactFocusOut = () => {
    if (this.#contactEditing) return;
    const item = this.#state.activeItem;
    const submenu = findSubmenu(item);
    if (!submenu?.querySelector('.sm-contact-menu')) return;
    requestAnimationFrame(() => {
      if (!this.isConnected || this.#state.activeItem !== item) return;
      if (submenu.contains(document.activeElement) || item.contains(document.activeElement)) return;
      if (submenu.matches(':hover') || item.matches(':hover')) return;
      this.closeDropdown();
    });
  };

  #outsidePointerDown = (event) => {
    const path = event.composedPath();
    const active = this.#state.activeItem;
    const submenu = findSubmenu(active);
    const contact = submenu?.querySelector('.sm-contact-menu');
    if (path.includes(this)) {
      if (contact) {
        const fieldClick = path.some(node => node instanceof Element && node.matches('input,select,textarea') && contact.contains(node));
        const label = path.find(node => node instanceof HTMLLabelElement && contact.contains(node));
        if (fieldClick || label?.control) {
          this.#contactEditing = true;
          clearTimeout(this.#mouseCloseTimer);
        } else {
          // A click on the photo, heading, or form background ends editing.
          // Otherwise the previous field can retain focus and pin the menu open.
          this.#contactEditing = false;
          const focused = document.activeElement;
          if (focused instanceof HTMLElement && contact.contains(focused) && focused.matches('input,select,textarea')) {
            focused.blur();
          }
        }
      }
      return;
    }
    if (!active) return;
    if (contact && this.#contactEditing) {
      // Ignore native UI retargeting a suggestion click to the document.
      // A real page click must land outside the form and its trigger.
      const target = event.target;
      const hit = document.elementFromPoint(event.clientX,event.clientY);
      if (contact.contains(hit) || active.contains(hit)) return;
      if (!(target instanceof Element)) return;
      if (target === document.body || target === document.documentElement) {
        // Wait for browser focus handling: a real background click blurs the
        // field; a native saved-text selection normally keeps focus there.
        requestAnimationFrame(() => {
          if (this.#state.activeItem !== active) return;
          const focused = document.activeElement;
          if (focused instanceof Element && contact.contains(focused) && focused.matches('input,select,textarea')) return;
          this.closeDropdown();
        });
        return;
      }
    }
    this.closeDropdown();
  };

  #escapeKey = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    const item = this.#state.activeItem;
    const focusedInside = this.contains(document.activeElement);
    this.closeDropdown();
    if (focusedInside) item.focus({ preventScroll: true });
  };

  closeDropdown = () => {
    this.#contactEditing = false;
    clearTimeout(this.#mouseCloseTimer);
    this.#cleanupMutationObserver();
    this.#clickOpen = false;
    this.#deactivate(this.#state.activeItem, true);
  };

  toggle = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('[ref="menuitem"]');
    if (!item || item.closest('header-menu') !== this) return;
    const submenu = findSubmenu(item);
    if (!submenu) return;
    // Only the parent trigger toggles. Product links and YMM controls never do.
    event.preventDefault();
    event.stopPropagation();
    // Every input type can close an already-open top-level menu.
    if (this.#state.activeItem === item && item.ariaExpanded === 'true') {
      this.closeDropdown();
      return;
    }
    const mouseClick = event.pointerType === 'mouse' || (!event.pointerType && event.detail > 0 && window.matchMedia('(hover: hover) and (pointer: fine)').matches && !event.sourceCapabilities?.firesTouchEvents);
    this.#clickOpen = !mouseClick;
    this.activate({ target: item.parentElement, menuItem: item, menuSubmenu: submenu });
  };

  activate = (event) => {
    clearTimeout(this.#mouseCloseTimer);
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Internal hover entry can pass its resolved nodes to avoid duplicate DOM lookups.
    let item = event.menuItem || findMenuItem(event.target);

    if (!item) return;
    // Duplicate entry/focus events must not cancel the opening fade.
    if (item !== this.#state.activeItem || this.#fadeClosing === item) {
      this.#cancelMenuFade();
    }

    // ui-custom.js adds this legacy visibility lock on outside pointerdown.
    // Opening by hover or keyboard must clear it just as clicking inside does.
    this.classList.remove('menu-force-closed');
    this.headerComponent.querySelectorAll('.header__underlay-open.menu-force-closed')
      .forEach(underlay => underlay.classList.remove('menu-force-closed'));

    if (item == this.#state.activeItem) return;

    // Read stable geometry before changing expanded state and header styles.
    // A hidden-but-laid-out submenu can be measured without revealing it first.
    const openingSubmenu = event.menuSubmenu || findSubmenu(item);
    const openingSubmenuHeight = openingSubmenu?.offsetHeight || 0;
    const openingHeader = this.headerComponent;
    const openingHeaderRow = openingHeader.hasAttribute('data-submenu-overlap-bottom-row')
      ? openingHeader.querySelector('.header__row--top') || openingHeader
      : openingHeader;
    const openingHeaderHeight = openingHeaderRow.offsetHeight;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#contactEditing = false;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = openingSubmenu;
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      this.#cleanupMutationObserver();

      let heightFrame = 0;
      this.#submenuMutationObserver = new MutationObserver(() => {
        if (heightFrame) return;
        heightFrame = requestAnimationFrame(() => {
          heightFrame = 0;
          if (!this.isConnected || this.#state.activeItem !== item) return;
          const height = submenu.offsetHeight;
          if (height > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
            this.#cleanupMutationObserver();
          }
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    // Keep the original post-open measurement as a fallback for display:none
    // content and overflow panels, but avoid it for already-laid-out panels.
    let finalHeight = (submenu === openingSubmenu ? openingSubmenuHeight : 0) || submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, openingHeaderHeight);
    
this.style.setProperty('--submenu-opacity', '1');

/* Fade only when opening the first menu.
   Switch directly between menus so the panel catches up to the mouse. */
if (
  !previouslyActiveItem &&
  submenu &&
  typeof submenu.animate === 'function' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches
) {
  this.#menuFade = submenu.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: 100,
    easing: 'cubic-bezier(.22,.61,.36,1)',
  });

  const animation = this.#menuFade;
  animation.onfinish = () => {
    if (this.#menuFade === animation) this.#menuFade = null;
  };
}
    if (this.#clickOpen) {
      document.body.removeEventListener('pointermove', this.#onPointerMove);
      if (previouslyActiveItem) this.#stopPointerTracking(previouslyActiveItem);
    } else {
      this.#startPointerTracking(item, previouslyActiveItem);
    }
  };

  #mouseCloseTimer;

  deactivate(event) {
    if (this.#clickOpen) return;
    if (!(event.target instanceof Element)) return;

    const contactMenu = findSubmenu(this.#state.activeItem);
    if (contactMenu?.querySelector('.sm-contact-menu')) {
      if (this.#contactEditing) return;
      // Do not close during blur/focus transfers between contact fields.
      // The focusout handler checks the final focus destination instead.
      if (event.type === 'blur' || contactMenu.contains(document.activeElement)) return;
    }

    if (event.type === 'pointerleave' && event.pointerType === 'mouse') {
      clearTimeout(this.#mouseCloseTimer);
      const activeItem = this.#state.activeItem;
      const submenu = activeItem ? findSubmenu(activeItem) : null;
      if (event.relatedTarget instanceof Node && submenu?.contains(event.relatedTarget)) return;
      this.#mouseCloseTimer = setTimeout(() => {
        this.#mouseCloseTimer = undefined;
        if (this.#state.activeItem !== activeItem) return;
        if (activeItem?.matches(':hover') || submenu?.matches(':hover') || submenu?.contains(document.activeElement)) return;
        this.#deactivate(activeItem);
      }, 80);
      return;
    }

    // Don't deactivate when pointer moves to body (underlay area)
    if (event.relatedTarget === document.body || event.relatedTarget === null) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu = event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu = event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {

      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem, force = false, skipFade = false) => {
    if (this.#clickOpen && !force) return;
    if (!item || item != this.#state.activeItem) return;

    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;
    const contactSubmenu = findSubmenu(item);
    if (!force && contactSubmenu?.querySelector('.sm-contact-menu') && (this.#contactEditing || contactSubmenu.contains(document.activeElement))) return;

    // Keep the existing geometry intact until opacity finishes fading out.
    // Explicit clicks/Escape remain immediate; hover departures fade softly.
    if (!force && !skipFade && contactSubmenu && typeof contactSubmenu.animate === 'function' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (this.#fadeClosing === item) return;
      const opacity = getComputedStyle(contactSubmenu).opacity;
      this.#cancelMenuFade();
      this.#fadeClosing = item;
      const animation = contactSubmenu.animate([{ opacity }, { opacity: 0 }], {
        duration: 160,
        easing: 'cubic-bezier(.4,0,1,1)',
        fill: 'forwards',
      });
      this.#menuFade = animation;
      animation.onfinish = () => {
        if (this.#menuFade !== animation) return;
        this.#menuFade = null;
        this.#fadeClosing = null;
        this.#deactivate(item, true, true);
        animation.cancel();
      };
      return;
    }
    this.#cancelMenuFade();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    // Measure the overflow link list, excluding absolutely positioned submenus.
    let height = 0;
    slottedMenuLinks.forEach((link) => {
      height = Math.max(height, link.offsetTop + link.offsetHeight);
    });
    return height || this.overflowMenu?.offsetHeight || 0;
  }

  #setFullOpenHeaderHeight(submenuHeight, measuredHeaderHeight) {
    if (!this.headerComponent) return;
    // Closing does not need to read layout after resetting header styles.
    if (submenuHeight === 0) {
      this.headerComponent.style.setProperty('--full-open-header-height', '0px');
      return;
    }
    if (Number.isFinite(measuredHeaderHeight)) {
      this.headerComponent.style.setProperty('--full-open-header-height', `${submenuHeight + measuredHeaderHeight}px`);
      return;
    }

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

